import { useCallback, useState } from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material"

export interface RenameDashboardDialogProps {
  open: boolean
  // The dashboard's current name — the field is prefilled with it so the user edits in place.
  currentName: string
  onClose: () => void
  // Resolves → dialog closes; rejects → dialog stays open with the inline error.
  onRename: (name: string) => Promise<void>
}

export default function RenameDashboardDialog({
  open,
  currentName,
  onClose,
  onRename,
}: RenameDashboardDialogProps) {
  // Seeded at mount; the parent remounts per open, so no prop→state sync effect (avoids set-state-in-effect).
  const [name, setName] = useState(currentName)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleClose = useCallback(() => {
    if (isSaving) return
    onClose()
  }, [isSaving, onClose])

  const trimmedName = name.trim()
  // Save is disabled when empty, unchanged, or a request is in flight.
  const isSaveDisabled = trimmedName.length === 0 || trimmedName === currentName.trim() || isSaving

  const handleSave = useCallback(async () => {
    if (isSaveDisabled) return
    setIsSaving(true)
    setError(null)
    try {
      await onRename(trimmedName)
      setIsSaving(false)
      onClose()
    } catch {
      // Keep the dialog open so the user can retry without re-typing.
      setError("Couldn't rename the dashboard. Please try again.")
      setIsSaving(false)
    }
  }, [isSaveDisabled, trimmedName, onRename, onClose])

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Rename dashboard</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          required
          fullWidth
          margin="dense"
          label="Dashboard name"
          value={name}
          onChange={(event) => { setName(event.target.value) }}
          error={error !== null}
          helperText={error ?? undefined}
          disabled={isSaving}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              void handleSave()
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>Cancel</Button>
        <Button onClick={() => { void handleSave() }} disabled={isSaveDisabled} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
